import React from 'react';
import './NumberInput.css';

/**
 * NumberInput Component
 * Standardized number input with increment/decrement controls
 * 
 * @param {string} label - Input label
 * @param {number} value - Input value
 * @param {function} onChange - Change handler: (value: number) => void
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} step - Step value (default: 1)
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} required - Required field indicator
 * @param {string} placeholder - Placeholder text
 * @param {string} helperText - Helper text below input
 * @param {string} className - Additional CSS classes
 */
const NumberInput = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  error,
  disabled = false,
  required = false,
  placeholder,
  helperText,
  className = ''
}) => {
  const handleChange = (e) => {
    const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleIncrement = () => {
    if (!disabled && value !== null && value !== undefined) {
      const newValue = max !== undefined ? Math.min(value + step, max) : value + step;
      if (onChange) {
        onChange(newValue);
      }
    } else if (!disabled && (value === null || value === undefined)) {
      if (onChange) {
        onChange(min !== undefined ? min : step);
      }
    }
  };

  const handleDecrement = () => {
    if (!disabled && value !== null && value !== undefined) {
      const newValue = min !== undefined ? Math.max(value - step, min) : value - step;
      if (onChange) {
        onChange(newValue);
      }
    } else if (!disabled && (value === null || value === undefined)) {
      if (onChange) {
        onChange(min !== undefined ? min : 0);
      }
    }
  };

  const inputId = `number-input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`number-input-container ${error ? 'error' : ''} ${className}`.trim()}>
      {label && (
        <label htmlFor={inputId} className="number-input-label">
          {label}
          {required && <span className="number-input-required">*</span>}
        </label>
      )}
      <div className="number-input-wrapper">
        <button
          type="button"
          className="number-input-button number-input-decrement"
          onClick={handleDecrement}
          disabled={disabled || (min !== undefined && value !== null && value !== undefined && value <= min)}
          aria-label="Decrease value"
        >
          −
        </button>
        <input
          type="number"
          id={inputId}
          value={value !== null && value !== undefined ? value : ''}
          onChange={handleChange}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          className={`number-input ${error ? 'error' : ''}`.trim()}
          aria-required={required}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
        />
        <button
          type="button"
          className="number-input-button number-input-increment"
          onClick={handleIncrement}
          disabled={disabled || (max !== undefined && value !== null && value !== undefined && value >= max)}
          aria-label="Increase value"
        >
          +
        </button>
      </div>
      {(error || helperText) && (
        <div className="number-input-footer">
          {error && (
            <span id={`${inputId}-error`} className="number-input-error" role="alert">
              {error}
            </span>
          )}
          {!error && helperText && (
            <span id={`${inputId}-helper`} className="number-input-helper">
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default NumberInput;










